import { useMemo, useState } from "react";
import { Bell, Cake, Plus } from "lucide-react";
import type { Reminder } from "@hub/shared";
import { Avatar, Button, EmptyState, Spinner, Tabs, BIRTHDAY_DATE, formatDate } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import { useOpenContact, useSelection } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import { ReminderRow } from "@/features/contact/ContactSections";
import { ReminderEditor } from "@/features/contact/ReminderEditor";
import s from "../shared.module.css";

type Tab = "upcoming" | "overdue" | "completed" | "birthdays";

export function RemindersPage() {
  const [tab, setTab] = useState<Tab>("upcoming");
  const [editing, setEditing] = useState<Reminder | "new" | null>(null);
  const open = useOpenContact();
  const focused = useSelection((st) => st.sidebarContactId ?? st.focusedContactId);
  const reminders = useEngineQuery("getReminders", { includeProcessed: true, limit: 500 });
  const birthdays = useEngineQuery("getUpcomingBirthdays", { days: 60 }, { enabled: tab === "birthdays" });
  const now = Date.now();
  const groups = useMemo(() => {
    const all = reminders.data?.items ?? [];
    return {
      upcoming: all.filter((r) => !r.isProcessed && new Date(r.fireAt).getTime() >= now),
      overdue: all.filter((r) => !r.isProcessed && new Date(r.fireAt).getTime() < now),
      completed: all.filter((r) => r.isProcessed).sort((a, b) => (a.processedAt ?? "") < (b.processedAt ?? "") ? 1 : -1),
    };
  }, [reminders.data, now]);
  const list = tab === "birthdays" ? [] : groups[tab];
  return (
    <Page title="Reminders" actions={<Button size="sm" variant="primary" icon={<Plus size={13} />} onClick={() => setEditing("new")}>New reminder</Button>}>
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: "upcoming", label: `Upcoming (${groups.upcoming.length})` },
          { id: "overdue", label: `Overdue (${groups.overdue.length})` },
          { id: "completed", label: "Completed" },
          { id: "birthdays", label: "Birthdays" },
        ]}
      />
      <div style={{ marginTop: 12 }}>
        {reminders.isLoading && <Spinner />}
        {tab !== "birthdays" && reminders.isSuccess && list.length === 0 && (
          <EmptyState icon={<Bell size={26} className="u-muted" />} title={tab === "overdue" ? "Nothing overdue" : tab === "completed" ? "Nothing completed yet" : "No upcoming reminders"}>
            {tab === "upcoming" ? "Add one from a person, a note with a date, or the button above." : ""}
          </EmptyState>
        )}
        {tab !== "birthdays" && list.map((r) => <ReminderRow key={r.id} reminder={r} showContact onEdit={() => setEditing(r)} />)}
        {tab === "birthdays" && (
          <>
            {birthdays.isLoading && <Spinner />}
            {birthdays.isSuccess && birthdays.data.length === 0 && <EmptyState icon={<Cake size={26} className="u-muted" />} title="No birthdays in the next two months">Birthdays come from your sources or the person's profile.</EmptyState>}
            {(birthdays.data ?? []).map((b) => (
              <div key={b.id} className="u-row" style={{ padding: "8px 0", borderBottom: "1px solid var(--border)", cursor: "pointer" }} onClick={() => open(b.contactId)}>
                <Avatar name={b.contact.displayName} src={b.contact.avatarUrl} size={30} tier={b.contact.bucket} seed={b.contactId} />
                <div className="u-grow">
                  <div style={{ fontWeight: 500 }}>{b.contact.displayName}</div>
                  <div className="u-small u-muted">{b.contact.headline}</div>
                </div>
                <span className={s.rowMeta}>{formatDate(b.nextOccurrence, BIRTHDAY_DATE)}{b.year ? ` (turns ${new Date(b.nextOccurrence!).getUTCFullYear() - b.year})` : ""}</span>
              </div>
            ))}
          </>
        )}
      </div>
      <ReminderEditor open={editing !== null} onClose={() => setEditing(null)} reminder={editing === "new" ? null : editing} contactId={editing === "new" ? focused : undefined} />
    </Page>
  );
}
